var LvValidator_1;
import { __decorate, __metadata } from "tslib";
import { Component, Inject, Prop, Provide } from "vue-property-decorator";
import * as validator from "class-validator";
import { debounce } from "lodash-es";
import { MapUtils } from "@leavy/utils";
import { UIComponent } from "../UIComponent";
import { DEFAULT_LOGGER } from "@fastoche/ui-core/logger";
import { ValidationMetadataProvider } from "@fastoche/ui-core/validator/metadata/ValidationMetadataProvider";
export function defaultValidatorOptions() {
    return {
        debounce: 300,
    };
}
export const SYM_LV_VALIDATOR = Symbol("LvValidator");
export var ValidatorInteractionState;
(function (ValidatorInteractionState) {
    ValidatorInteractionState["INITIAL"] = "initial";
    ValidatorInteractionState["SUBMITTED"] = "submitted";
})(ValidatorInteractionState || (ValidatorInteractionState = {}));
export const COMPONENT_NAME = "LvValidator";
let LvValidator = LvValidator_1 = class LvValidator extends UIComponent {
    constructor() {
        super(...arguments);
        this.validatorId = 0;
        this.metadataProvider = ValidationMetadataProvider.default;
        this.propertyWatchers = new Map();
        this.children = [];
        this.innerInteractionState = ValidatorInteractionState.INITIAL;
    }
    get self() {
        return this;
    }
    get interactionState() {
        return this.innerInteractionState;
    }
    created() {
        this.validatorId = LvValidator_1.count++;
        this.registerToParent();
    }
    beforeDestroy() {
        LvValidator_1.count--;
        this.validatorId = -1;
        this.unregisterFromParent();
    }
    debug(message, meta) {
        if (LvValidator_1.DEBUG) {
            DEFAULT_LOGGER.debug(`[LvValidator] [${this.validatorId}] ${message}`, meta);
        }
    }
    get mergedOptions() {
        return {
            ...defaultValidatorOptions(),
            ...this.options,
            ...this.parent?.mergedOptions,
        };
    }
    registerToParent() {
        // If this is a root validator, it does not have any parent.
        if (this.root)
            return;
        this.debug("Has parent", { hasParent: !!this.parent });
        if (this.parent) {
            this.parent.children.push(this);
        }
    }
    unregisterFromParent() {
        // If this is a root validator, it does not have any parent.
        if (this.root)
            return;
        if (this.parent) {
            const idx = this.parent.children.indexOf(this);
            this.parent.children.splice(idx, 1);
        }
    }
    registerObserver(prop, observer) {
        this.debug("Register observer", { prop, observer });
        const watcher = this.propertyWatchers.get(prop) ?? { observers: [] };
        if (!watcher.observers.includes(observer))
            watcher.observers.push(observer);
        if (!watcher.releaseWatcher) {
            watcher.releaseWatcher = this.createPropertyWatcher(prop);
        }
        this.propertyWatchers.set(prop, watcher);
    }
    unregisterObserver(prop, observer) {
        this.debug("Unregister observer", { prop, observer });
        const watcher = this.propertyWatchers.get(prop);
        if (!watcher)
            return;
        const idx = watcher.observers.indexOf(observer);
        if (idx != -1)
            watcher.observers.splice(idx, 1);
        if (watcher.observers.length == 0) {
            if (watcher.releaseWatcher)
                watcher.releaseWatcher();
            this.propertyWatchers.delete(prop);
        }
    }
    createPropertyWatcher(prop) {
        this.debug("Create property watcher", { prop });
        const watchPath = `model.${prop}`;
        const watchHandler = (newValue, oldValue) => {
            if (newValue != oldValue) {
                void this.validateProperty(prop);
            }
        };
        const debounceMs = this.mergedOptions.debounce;
        if (debounceMs !== false) {
            return this.$watch(watchPath, debounce(watchHandler, debounceMs));
        }
        else {
            return this.$watch(watchPath, watchHandler);
        }
    }
    async validateProperty(prop) {
        this.debug("Validate property", { prop });
        const propertyWatcher = this.propertyWatchers.get(prop);
        if (!propertyWatcher) {
            return;
        }
        const newValue = this.model[prop];
        const propertyDependencies = this
            .metadataProvider
            .getPropertyDependencies(this.model, prop)
            .map(x => [x, this.model[x]])
            .reduce((previous, [key, value]) => {
            previous[key] = value;
            return previous;
        }, {});
        const validationModel = {
            ...propertyDependencies,
            [prop]: newValue,
            constructor: this.model.constructor,
        };
        this.debug("Partial model to validate", { validationModel });
        const validationErrors = await validator.validate(validationModel, {
            ...this.mergedOptions,
            skipMissingProperties: true,
        });
        this.debug("Partial validation errors", { validationErrors });
        const validationResult = validationErrors
            .filter(x => x.property == prop)
            .map(x => x.constraints ?? {})
            .filter(x => !!x)
            .reduce((prev, curr) => ({ ...prev, ...curr }), {});
        propertyWatcher.observers.forEach(x => x.onValidationChange(validationResult));
    }
    async validateModel() {
        this.debug("Validate model");
        const errors = await validator.validate(this.model, this.mergedOptions);
        const modelValidationResult = errors
            .flatMap(x => this.walkValidationError(x))
            .reduce((prev, curr) => {
            return {
                ...prev,
                ...curr,
            };
        }, {});
        for (const [path, constraints] of Object.entries(modelValidationResult)) {
            this.notifyPropertyValidationChanged(path, constraints);
        }
        this.debug("Model validation result", { modelValidationResult });
        return modelValidationResult;
    }
    walkValidationError(error, lastPath) {
        this.debug("Walk validation error", { error, lastPath });
        const currentPath = [lastPath, error.property].filter(x => x !== undefined).join(".");
        const validationResult = {
            [currentPath]: error.constraints ?? {},
        };
        return [
            validationResult,
            ...(error.children ?? []).flatMap(x => this.walkValidationError(x, currentPath)),
        ];
    }
    notifyPropertyValidationChanged(propertyFullPath, validationResult) {
        this.debug("Notify property validation changed", { propertyFullPath, validationResult });
        if (this.propertyWatchers.has(propertyFullPath)) {
            const watcher = MapUtils.getOrThrow(this.propertyWatchers, propertyFullPath);
            for (const sub of watcher.observers) {
                sub.onValidationChange(validationResult);
            }
        }
    }
    /**
     * Validate the model and sets the form as submitted.
     * @returns `true` if the model is valid, else `false`
     */
    async submit() {
        this.innerInteractionState = ValidatorInteractionState.SUBMITTED;
        for (const child of this.children)
            child.innerInteractionState = ValidatorInteractionState.SUBMITTED;
        const result = await this.validate();
        return Object.values(result).length == 0;
    }
    /**
     * Validate the model
     */
    validate() {
        for (const child of this.children) {
            void child.validate();
        }
        return this.validateModel();
    }
    /**
     * Reset the validation state
     */
    reset() {
        this.innerInteractionState = ValidatorInteractionState.INITIAL;
        for (const child of this.children)
            child.innerInteractionState = ValidatorInteractionState.INITIAL;
        for (const [propertyKey, propertyWatcher] of this.propertyWatchers) {
            for (const observer of propertyWatcher.observers) {
                observer.onValidationChange(null);
            }
        }
        for (const child of this.children) {
            child.reset();
        }
    }
    render(h) {
        return <div class="lv-validator">{this.$slots.default}</div>;
    }
};
/**
 * @internal
 */
LvValidator.DEBUG = false;
LvValidator.count = 0;
__decorate([
    Provide(SYM_LV_VALIDATOR),
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], LvValidator.prototype, "self", null);
__decorate([
    Inject({ default: null, from: SYM_LV_VALIDATOR }),
    __metadata("design:type", Object)
], LvValidator.prototype, "parent", void 0);
__decorate([
    Prop({ type: Object }),
    __metadata("design:type", Object)
], LvValidator.prototype, "model", void 0);
__decorate([
    Prop({ type: Object, required: false, default: () => null }),
    __metadata("design:type", Object)
], LvValidator.prototype, "options", void 0);
__decorate([
    Prop({ type: Boolean, required: false, default: () => false }),
    __metadata("design:type", Boolean)
], LvValidator.prototype, "root", void 0);
LvValidator = LvValidator_1 = __decorate([
    Component({
        name: COMPONENT_NAME,
    })
], LvValidator);
export { LvValidator };
export default LvValidator;
//# sourceMappingURL=LvValidator.jsx.map